import React, { useState, useEffect, useRef, useMemo } from "react";

const CustomToolbar = ({
  label,
  onNavigate,
  onView,
  views,
  onShowHolidays,
  region,
  groups,
  areasMarked,
  selectedGroupFilter,
  setSelectedGroupFilter,
  selectedPSLCheckboxes,
  setSelectedPSLCheckboxes
}) => {
  const [, setAvailablePSLs] = useState([]);
  const [, setIsPSLDropdownOpen] = useState(false);
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
  const pslRef = useRef(null);
  const groupRef = useRef(null);

  /* =========================================================
     🆕 NEW: FILTER GROUPS THAT HAVE AT LEAST ONE PSL
     (No existing functionality touched)
  ========================================================= */
  const groupsWithPSL = useMemo(() => {
    if (!Array.isArray(groups) || !Array.isArray(areasMarked)) return groups || [];

    return groups.filter(group =>
      areasMarked.some(area =>
        area.Name === "PSL - Parking Space Location" &&
        (area.groupName || "Default") === (group.name || "Default")
      )
    );
  }, [groups, areasMarked]);

  /* =========================================================
     🆕 NEW: AUTO FIX SELECTED GROUP IF PSL IS REMOVED
  ========================================================= */
  useEffect(() => {
    if (!groupsWithPSL.length) return;

    const exists = groupsWithPSL.some(
      g => (g.name || "Default") === selectedGroupFilter
    );

    if (!exists) {
      setSelectedGroupFilter(groupsWithPSL[0].name || "Default");
    }
  }, [groupsWithPSL, selectedGroupFilter, setSelectedGroupFilter]);
  /* ---------------------------------------------------------
     CLOSE DROPDOWNS WHEN CLICKING OUTSIDE (UNCHANGED)
  --------------------------------------------------------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pslRef.current &&
        !pslRef.current.contains(event.target) &&
        !event.target.closest(".psl-dropdown")
      ) {
        setIsPSLDropdownOpen(false);
      }

      if (
        groupRef.current &&
        !groupRef.current.contains(event.target) &&
        !event.target.closest(".group-dropdown")
      ) {
        setIsGroupDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------------------------------------------------
     UPDATE PSL LIST BASED ON SELECTED GROUP (UNCHANGED)
  --------------------------------------------------------- */
  useEffect(() => {
    if (!areasMarked || !selectedGroupFilter) {
      setAvailablePSLs([]);
      return;
    }
    const groupPSLs = areasMarked
      .filter((area) => {
        const isPSL = area.Name === "PSL - Parking Space Location";
        const belongsToGroup =
          area.groupName === selectedGroupFilter ||
          area.group === selectedGroupFilter ||
          (!area.groupName && selectedGroupFilter === "Default");
        return isPSL && belongsToGroup;
      })
      .map((area) => ({
        id: area.id,
        name: area.pslName || `PSL ${area.id}`,
        groupName: area.groupName || "Default",
      }));

    setAvailablePSLs(groupPSLs);
  }, [selectedGroupFilter, areasMarked]);

  /* ---------------------------------------------------------
     NAVIGATION (UNCHANGED)
  --------------------------------------------------------- */
  const goToBack = () => onNavigate("PREV");
  const goToNext = () => onNavigate("NEXT");
  const goToToday = () => onNavigate("TODAY");

  const holidayButtonText =
    region === "US" ? "Show U.S. Holidays" : "Show Holidays";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "nowrap",
        gap: "10px",
        padding: "8px 12px",
        backgroundColor: "#fff",
      }}
    >
      {/* LEFT SIDE */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
        <span style={{ fontWeight: "700", fontSize: "16px", minWidth: "120px" }}>
          {label}
        </span>

        <button onClick={goToToday}>Today</button>

        <button onClick={onShowHolidays}>
          {holidayButtonText}
        </button>

        {/* GROUP DROPDOWN */}
        <div
          ref={groupRef}
          className="group-dropdown"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            whiteSpace: "nowrap",
          }}
        >
          <label style={{ fontWeight: "600", fontSize: "16px" }}>
            Group Name:
          </label>

          <button
            onClick={() => setIsGroupDropdownOpen(!isGroupDropdownOpen)}
            style={{
              width: "150px",
              padding: "6px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "13px",
              backgroundColor: "#fff",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selectedGroupFilter || "Select"}
            </span>
            <span style={{ fontSize: 10 }}>
              {isGroupDropdownOpen ? "▲" : "▼"}
            </span>
          </button>

          {isGroupDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "105px",
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "6px",
                marginTop: "2px",
                boxShadow: "0 4px 8px rgba(70,58,200,0.08)",
                zIndex: 1000,
                maxHeight: "220px",
                overflowY: "auto",
                width: "150px",
              }}
            >
              {(groupsWithPSL || []).map((group, index) => {
                const value =
                  group.name || group.groupName || group || "Default";

                return (
                  <div
                    key={group.id || index}
                    onClick={() => {
                      setSelectedGroupFilter(value);
                      setSelectedPSLCheckboxes([]);
                      setAvailablePSLs([]);
                      setIsGroupDropdownOpen(false);
                    }}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor:
                        selectedGroupFilter === value ? "#e5e7eb" : "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {value}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <button onClick={goToBack}>‹</button>
        <button onClick={goToNext}>›</button>
        {views.map((viewName) => (
          <button key={viewName} onClick={() => onView(viewName)}>
            {viewName}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CustomToolbar;
